<script setup>
/**
 * Yggdrasil — procedural line-art world-tree.
 *
 * The canopy is grown by a seeded recursive algorithm (deterministic:
 * the same tree on every load) instead of hand-placed paths: 7 master
 * limbs, 4 branching levels, almond leaves sprouting in pairs along the
 * twigs and in fans at the tips — matching the density of the reference
 * art. Trunk and roots stay hand-drawn.
 *
 * Wind: limbs and twigs are nested animation groups swaying around
 * their own attachment point (transform-origin in viewBox units).
 * The `windy` prop only raises the registered @property amplitudes,
 * so gusts build up smoothly without restarting animations.
 */
defineProps({
  windy: { type: Boolean, default: false }
})

// --- deterministic PRNG (mulberry32) — seed chosen for a good silhouette
function mulberry32(seed) {
  return function () {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}
const rand = mulberry32(11)

const rad = (d) => (d * Math.PI) / 180
const r1 = (n) => Math.round(n * 10) / 10

// point at t along a quadratic bezier
function qPoint(x0, y0, cx, cy, x1, y1, t) {
  const u = 1 - t
  return [u * u * x0 + 2 * u * t * cx + t * t * x1, u * u * y0 + 2 * u * t * cy + t * t * y1]
}

function leafTransform(x, y, dirDeg, scale) {
  // the leaf path points along +x; SVG rotate is clockwise (y-down)
  const rot = -dirDeg + (rand() - 0.5) * 30
  return `translate(${r1(x)} ${r1(y)}) rotate(${r1(rot)}) scale(${r1(scale)})`
}

const CHILDREN_PER_DEPTH = [3, 2, 2]
const MAX_DEPTH = 3
const LEAN = 9 // degrees — the whole canopy drifts left, like the reference

function grow(x, y, ang, len, width, depth, sink, sparse) {
  const bend = (rand() - 0.5) * 26 + LEAN
  const endAng = ang + bend
  // control point: halfway along the start direction, for an organic curve
  const cx = x + Math.cos(rad(ang)) * len * 0.55
  const cy = y - Math.sin(rad(ang)) * len * 0.55
  const ex = x + Math.cos(rad(endAng)) * len
  const ey = y - Math.sin(rad(endAng)) * len

  sink.paths.push({
    d: `M${r1(x)} ${r1(y)} Q${r1(cx)} ${r1(cy)} ${r1(ex)} ${r1(ey)}`,
    w: r1(Math.max(width, 0.7)),
    cls: depth <= 1 ? 'stroke-main' : 'stroke-dim'
  })

  // leaf pairs sprouting along thin twigs (sparser on the low limbs)
  if (depth >= 2 && (!sparse || rand() > 0.45)) {
    const t = 0.5 + rand() * 0.3
    const [px, py] = qPoint(x, y, cx, cy, ex, ey, t)
    sink.leaves.push(leafTransform(px, py, endAng + 65, 0.4 + rand() * 0.2))
    if (rand() > 0.4) sink.leaves.push(leafTransform(px, py, endAng - 65, 0.35 + rand() * 0.2))
  }

  if (depth >= MAX_DEPTH) {
    // terminal fan of leaves
    const n = (sparse ? 1 : 2) + Math.floor(rand() * (sparse ? 2 : 3))
    for (let i = 0; i < n; i++) {
      sink.leaves.push(leafTransform(ex, ey, endAng - 50 + i * (100 / Math.max(n - 1, 1)), 0.45 + rand() * 0.3))
    }
    return
  }

  const n = CHILDREN_PER_DEPTH[depth] + (rand() > 0.75 ? 1 : 0)
  for (let i = 0; i < n; i++) {
    const t = 0.45 + (i / n) * 0.5 + rand() * 0.08
    const [bx, by] = qPoint(x, y, cx, cy, ex, ey, Math.min(t, 1))
    const spread = (i - (n - 1) / 2) * (26 + rand() * 10) + (rand() - 0.5) * 14
    grow(bx, by, endAng + spread, len * (0.6 + rand() * 0.16), width * 0.62, depth + 1, sink, sparse)
  }
}

// master limbs — attachment points sit on the hand-drawn trunk; the
// canopy masses at the crown and drifts left, the low limbs droop long
// and sparse, the right side stays restrained — like the reference art
const LIMBS = [
  { x: 500, y: 455, ang: 176, len: 150, w: 2.0, sparse: true },
  { x: 512, y: 330, ang: 142, len: 124, w: 1.9 },
  { x: 518, y: 235, ang: 118, len: 126, w: 1.8 },
  { x: 522, y: 175, ang: 94, len: 122, w: 1.8 },
  { x: 530, y: 262, ang: 60, len: 92, w: 1.7 },
  { x: 540, y: 395, ang: 18, len: 88, w: 1.6, sparse: true },
  { x: 498, y: 540, ang: 188, len: 76, w: 1.4, sparse: true }
]

const limbs = LIMBS.map(({ x, y, ang, len, w, sparse }) => {
  const limb = {
    origin: `${x}px ${y}px`,
    dur: r1(7 + rand() * 2.5),
    delay: r1(-rand() * 8),
    paths: [],
    twigs: []
  }

  // depth-0 spine drawn into the limb itself, children become twig groups
  const bend = (rand() - 0.5) * 20 + LEAN
  const endAng = ang + bend
  const cx = x + Math.cos(rad(ang)) * len * 0.55
  const cy = y - Math.sin(rad(ang)) * len * 0.55
  const ex = x + Math.cos(rad(endAng)) * len
  const ey = y - Math.sin(rad(endAng)) * len
  limb.paths.push({
    d: `M${r1(x)} ${r1(y)} Q${r1(cx)} ${r1(cy)} ${r1(ex)} ${r1(ey)}`,
    w,
    cls: 'stroke-main'
  })

  const n = sparse ? 2 : CHILDREN_PER_DEPTH[0]
  for (let i = 0; i < n; i++) {
    const t = 0.4 + (i / n) * 0.55 + rand() * 0.06
    const [bx, by] = qPoint(x, y, cx, cy, ex, ey, Math.min(t, 1))
    const spread = (i - (n - 1) / 2) * (28 + rand() * 10) + (rand() - 0.5) * 12
    const twig = {
      origin: `${r1(bx)}px ${r1(by)}px`,
      dur: r1(3.6 + rand() * 1.8),
      delay: r1(-rand() * 5),
      paths: [],
      leaves: []
    }
    grow(bx, by, endAng + spread, len * (0.62 + rand() * 0.14), w * 0.62, 1, twig, sparse)
    limb.twigs.push(twig)
  }

  return limb
})
</script>

<template>
  <svg
    viewBox="0 0 640 800"
    fill="none"
    aria-hidden="true"
    class="yggdrasil w-full h-full"
    :class="{ windy }"
  >
    <defs>
      <!-- Almond leaf, stroke only -->
      <path id="ygg-leaf" d="M0 0 C5 -9 13 -14 22 -15 C20 -6 12 -1 0 0 Z" />
    </defs>

    <!-- ================= ROOTS ================= -->
    <g class="stroke-dim" stroke-width="1.6">
      <path d="M470 700 C438 728 396 742 338 748 C316 750 300 758 284 756" />
      <path d="M496 702 C476 736 448 756 408 764" />
      <path d="M520 704 C516 736 502 752 482 760" />
      <path d="M560 700 C592 726 620 738 640 742" />
      <path d="M544 702 C558 740 588 760 626 766" />
      <path d="M340 748 C330 742 326 734 330 726 C334 720 342 720 344 727" stroke-width="1.1" />
      <path d="M600 744 C610 738 612 730 606 724 C601 720 594 722 594 728" stroke-width="1.1" />
    </g>
    <!-- ground hints -->
    <g class="stroke-faint" stroke-width="1">
      <path d="M180 742 C300 726 380 724 470 716" />
      <path d="M560 716 C600 718 630 724 640 726" />
    </g>

    <!-- ================= TRUNK ================= -->
    <!-- four long lines flowing from the root flare up to the crown, gentle S -->
    <g class="stroke-main" stroke-width="2.2">
      <path d="M470 700 C470 600 486 500 500 430 C512 360 504 280 508 215 C510 196 514 180 520 168" />
      <path d="M560 700 C556 610 546 530 538 470 C528 400 528 300 524 232 C522 206 521 186 520 168" />
    </g>
    <g class="stroke-dim" stroke-width="1.3">
      <path d="M505 690 C500 600 508 500 516 440 C523 392 521 320 520 260" />
      <path d="M535 694 C534 620 528 540 524 480" />
      <path d="M492 560 C496 540 502 524 508 512" />
      <path d="M538 470 C534 452 532 434 533 418" />
    </g>

    <!-- ================= CANOPY (procedural) ================= -->
    <g
      v-for="(limb, i) in limbs"
      :key="i"
      class="branch"
      :style="`--dur:${limb.dur}s; --delay:${limb.delay}s; transform-origin:${limb.origin}`"
    >
      <path v-for="(p, j) in limb.paths" :key="j" :d="p.d" :class="p.cls" :stroke-width="p.w" />
      <g
        v-for="(twig, k) in limb.twigs"
        :key="k"
        class="twig"
        :style="`--dur:${twig.dur}s; --delay:${twig.delay}s; transform-origin:${twig.origin}`"
      >
        <path v-for="(p, j) in twig.paths" :key="j" :d="p.d" :class="p.cls" :stroke-width="p.w" />
        <use
          v-for="(lf, m) in twig.leaves"
          :key="m"
          href="#ygg-leaf"
          class="stroke-leaf"
          stroke-width="1.1"
          :transform="lf"
        />
      </g>
    </g>

    <!-- Detached drifting leaves -->
    <g class="twig floaters stroke-leaf" stroke-width="1.1" style="--dur: 5.6s; --delay: -1s; transform-origin: 150px 380px">
      <use href="#ygg-leaf" transform="translate(120 420) rotate(200) scale(0.7)" opacity="0.55" />
      <use href="#ygg-leaf" transform="translate(92 332) rotate(168) scale(0.55)" opacity="0.4" />
      <use href="#ygg-leaf" transform="translate(230 302) rotate(222) scale(0.6)" opacity="0.5" />
      <use href="#ygg-leaf" transform="translate(60 250) rotate(190) scale(0.5)" opacity="0.35" />
    </g>

    <!-- Canopy sparks -->
    <g fill="#d9c9ff" stroke="none" opacity="0.55">
      <circle cx="250" cy="240" r="1.4" />
      <circle cx="330" cy="120" r="1.1" />
      <circle cx="180" cy="440" r="1.2" />
      <circle cx="560" cy="150" r="1.1" />
      <circle cx="600" cy="320" r="1.3" />
      <circle cx="420" cy="90" r="1.1" />
      <circle cx="130" cy="330" r="1" />
    </g>
  </svg>
</template>

<style scoped>
@property --amp {
  syntax: '<angle>';
  inherits: true;
  initial-value: 0.45deg;
}
@property --twigamp {
  syntax: '<angle>';
  inherits: true;
  initial-value: 1.2deg;
}

.yggdrasil {
  --amp: 0.45deg;
  --twigamp: 1.2deg;
  transition:
    --amp 1.6s ease,
    --twigamp 1.6s ease;
  filter: drop-shadow(0 0 5px rgba(167, 139, 250, 0.5)) drop-shadow(0 0 22px rgba(124, 86, 231, 0.28));
}

.yggdrasil.windy {
  --amp: 2deg;
  --twigamp: 4.5deg;
}

.stroke-main { stroke: #b7a3f6; opacity: 0.8; }
.stroke-dim { stroke: #9d86ea; opacity: 0.55; }
.stroke-faint { stroke: #8d76e0; opacity: 0.3; }
.stroke-leaf { stroke: #cbbcff; opacity: 0.85; }

.yggdrasil path,
.yggdrasil use {
  stroke-linecap: round;
  stroke-linejoin: round;
}

/* transform-origin declared inline in viewBox units */
.branch,
.twig {
  transform-box: view-box;
}

.branch {
  animation: ygg-sway var(--dur, 8s) var(--delay, 0s) ease-in-out infinite alternate;
}

.twig {
  animation: ygg-twigsway var(--dur, 4s) var(--delay, 0s) ease-in-out infinite alternate;
}

.floaters {
  animation-name: ygg-drift;
}

@keyframes ygg-sway {
  from { transform: rotate(calc(var(--amp) * -1)); }
  to { transform: rotate(var(--amp)); }
}

@keyframes ygg-twigsway {
  from { transform: rotate(calc(var(--twigamp) * -1)); }
  to { transform: rotate(var(--twigamp)); }
}

@keyframes ygg-drift {
  from { transform: rotate(calc(var(--twigamp) * -1)) translate(0, 0); }
  to { transform: rotate(var(--twigamp)) translate(-8px, 6px); }
}

@media (prefers-reduced-motion: reduce) {
  .branch,
  .twig {
    animation: none;
  }
  .yggdrasil {
    transition: none;
  }
}
</style>
